import * as React from "react";
import { useState, useMemo } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { toast } from "sonner";
import { useSelector } from "react-redux";
import { useGetBatchQuery } from "../../../store/batchApi";
import {
  useGetAssignmentsQuery,
  useCreateAssignmentMutation,
  useUpdateAssignmentMutation,
  useDeleteAssignmentMutation,
} from "../../../store/assignmentApi";
import AssignmentListSection from "./assignments/AssignmentListSection";
import AssignmentsSectionSkeleton from "@/components/ui/AssignmentSectionSkeleton";
import AddAssignmentForm from "./assignments/AddAssignment";

const AssignmentsSection = () => {
  const { data: allBatch } = useGetBatchQuery();

  const [activeTab, setActiveTab] = useState<"list" | "form">("list");
  const [showForm, setShowForm] = useState(false);
  const [editingAssignment, setEditingAssignment] = useState(null);

  const [form, setForm] = useState({
    title: "",
    type: "assignment",
    link: "",
    batch: "",
    file: null,
    deadline: "",
    totalMarks: "",
    description: "",
  });

  const { data: assignments, isLoading } = useGetAssignmentsQuery({
    batchCode: form.batch,
    type: form.type,
  });

  const [createAssignment, { isLoading: creating }] =
    useCreateAssignmentMutation();
  const [updateAssignment, { isLoading: updating }] =
    useUpdateAssignmentMutation();

  const [deleteAssignment, { isLoading: deleting }] =
    useDeleteAssignmentMutation();
  const [expandedItems, setExpandedItems] = useState({});

  const toggleExpanded = (id: string) => {
    setExpandedItems((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  const { user } = useSelector((state) => state.user);

  const isValid = useMemo(() => {
    return form.title && form.batch && form.totalMarks && form.deadline;
  }, [form]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((p) => ({ ...p, [name]: value }));
  };

  const handleFile = (e) => {
    setForm((p) => ({ ...p, file: e.target.files?.[0] || null }));
  };

  const reset = () => {
    setForm({
      title: "",
      type: form.type,
      link: form.link,
      batch: "",
      file: null,
      deadline: "",
      description: "",
      totalMarks: "",
    });
    setEditingAssignment(null);
  };

  const handleEdit = (item) => {
    setActiveTab("form");
    setShowForm(true);
    setEditingAssignment(item);

    setForm({
      title: item.title,
      type: item.type,
      link: form.link,
      batch: item.batchCode?._id || "",
      file: item.uploadedFile || null,
      deadline: item.deadline
        ? new Date(item.deadline).toISOString().split("T")[0]
        : "",
      totalMarks: item.totalMarks?.toString() || "",
      description: item.description || "",
    });
  };

  const handleDelete = async (assignmentId: string) => {
    if (!assignmentId) return;

    const confirmDelete = window.confirm(
      "Are you sure you want to delete this assignment?"
    );

    if (!confirmDelete) return;

    try {
      const res = await deleteAssignment({ id: assignmentId }).unwrap();
      if (res?.success) {
        toast.success("Assignment deleted successfully");
      } else {
        toast.error(res?.error?.data?.message || "Failed to delete assignment");
      }
    } catch (err: any) {
      toast.error(err?.data?.message || "Failed to delete assignment");
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!isValid) {
      toast.error("Please fill all required fields");
      return;
    }

    try {
      if (editingAssignment) {
        const res = await updateAssignment({
          id: editingAssignment._id,
          formData: {
            title: form.title,
            type: form.type,
            link: form.link,
            description: form.description,
            totalMarks: form.totalMarks,
            deadline: form.deadline,
          },
        }).unwrap();

        if (res?.success) {
          setShowForm(false);
          toast.success("Updated successfully");
          setActiveTab("list");
        } else {
          toast.error(
            res?.error?.data?.message || "Failed To Update Assignment "
          );
        }
      } else {
        const fd = new FormData();
        fd.append("title", form.title);
        fd.append("type", form.type);
        fd.append("link", form.link);
        fd.append("batchCode", form.batch);
        fd.append("assignedBy", user?._id);
        fd.append("description", form.description);
        fd.append("totalMarks", form.totalMarks);
        fd.append("deadline", new Date(form.deadline).toISOString());
        if (form.file) fd.append("file", form.file);

        for (let [key, value] of fd.entries()) {
          console.log(key, value);
        }

        const res = await createAssignment(fd).unwrap();
        if (res?.data) {
          toast.success("Created successfully");
        } else {
          toast.error(
            res?.error?.data?.message || "Failed To Create Assignment "
          );
        }
      }

      reset();
      setShowForm(false);
      setActiveTab("list");
    } catch (err) {
      toast.error(err?.data?.message || "Failed");
    }
  };

  if (isLoading) return <AssignmentsSectionSkeleton />;

  return (
    <Card>
      <CardHeader>
        <CardTitle>
          <div className="flex border-b mb-4">
            <button
              onClick={() => {
                setActiveTab("list");
                reset();
              }}
              className={`px-4 py-2 ${activeTab === "list"
                  ? "border-b-2 border-black"
                  : "text-gray-500"
                }`}
            >
              Assignments
            </button>

            <button
              onClick={() => {
                setActiveTab("form");
                setShowForm(true);
              }}
              className={`px-4 py-2 ${activeTab === "form"
                  ? "border-b-2 border-black"
                  : "text-gray-500"
                }`}
            >
              Add Assignment
            </button>
          </div>
        </CardTitle>
      </CardHeader>

      <CardContent>
        {activeTab === "form" && showForm ? (
          <AddAssignmentForm
            form={form}
            allBatch={allBatch}
            editingAssignment={editingAssignment}
            creating={creating}
            updating={updating}
            handleChange={handleChange}
            handleFile={handleFile}
            handleSubmit={handleSubmit}
            reset={reset}
          />
        ) : (
          <AssignmentListSection
            form={form}
            allBatch={allBatch}
            assignments={assignments?.data || []}
            expandedItems={expandedItems}
            toggleExpanded={toggleExpanded}
            handleChange={handleChange}
            handleEdit={handleEdit}
            handleDelete={handleDelete}
          />
        )}
      </CardContent>
    </Card>
  );
};

export default AssignmentsSection;
